import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../shared/services/language.services';
import { Store } from '@ngrx/store';
import { getUserSelector, isAuthenticatedSelector, retryLoginErrorSelector } from '../../../user/store/user.selectors';
import { Observable } from 'rxjs';
import { IUser } from '../../../user/model/user.model';
import { selectTimer } from '../../store/timer.selector';
import { TimerState } from '../../store/timer.state';
import { environment } from '../../../environments/environment';
import { SsoService } from '../../../user/services/sso.service';

@Component({
    selector: 'nav-bar',
    templateUrl: './navbar.component.html',
    standalone: true,
    styleUrls: ['./navbar.component.scss'],
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
    ]
})
export class NavbarComponent {
    isMenuOpen = false;
    isLanguageMenuOpen = false;
    isUserMenuOpen = false;
    languageDescription!: string;
    private languageService = inject(LanguageService);
    private translate = inject(TranslateService);
    private store = inject(Store);
    private router = inject(Router);
    private sso = inject(SsoService);
    private elementRef = inject(ElementRef);
    studioUrl: string = environment.StudioUrl;

    isAuthenticated$: Observable<boolean> = this.store.select(isAuthenticatedSelector);
    retryLoginError$: Observable<string | undefined> = this.store.select(retryLoginErrorSelector);

    timerState$: Observable<TimerState> = this.store.select(selectTimer);
    timeLimitExceeded!: boolean;
    timeout: number | undefined;
    remainingTime!: number;

    user$: Observable<IUser> = this.store.select(getUserSelector);

    ngOnInit(): void {
        this.isMenuOpen = false;
        this.languageService.setLanguage();

        this.timerState$.subscribe(timer => {
            if (timer !== null && timer !== undefined &&
                timer.actualTime !== null && timer.actualTime !== undefined &&
                timer.startTime !== null && timer.startTime !== undefined) {
                const elapsedTime = (timer.actualTime.getMinutes() - timer.startTime.getMinutes());
                if (this.timeout !== undefined) {
                    this.remainingTime = this.timeout - elapsedTime;
                    if (elapsedTime >= this.timeout) {
                        this.timeLimitExceeded = true;
                    }
                }
            }
        });

        this.user$.subscribe(user => {
            if (user) {
                this.timeout = user.timeout;
            }
        });

        this.setLanguageDescription(this.languageService.getLanguage()!);
    }

    toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen;
    }

    toggleLanguageMenu(event: MouseEvent): void {
        event.preventDefault();
        this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
        this.isUserMenuOpen = false;
    }

    toggleUserMenu(event: MouseEvent): void {
        event.preventDefault();
        this.isUserMenuOpen = !this.isUserMenuOpen;
        this.isLanguageMenuOpen = false;
    }

    closeUserMenu(): void {
        this.isUserMenuOpen = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isLanguageMenuOpen = false;
            this.isUserMenuOpen = false;
        }
    }

    useLanguage(language: string): void {
        this.translate.use(language);
        this.translate.setDefaultLang(language);
        this.setLanguageDescription(language);
        localStorage.setItem('language', language);
        this.reloadPage();
    }

    setLanguageDescription(country: string): void {
        if (country === undefined || country === null || country === '') {
            country = 'en';
        }
        country = this.languageService.getLanguage() ?? 'en';

        switch (country) {
            case 'en':
                this.languageDescription = 'English';
                break;

            case 'pt-BR':
                this.languageDescription = 'Portugu\u00EAs (BR)';
                break;

            case 'pt-PT':
                this.languageDescription = 'Portugu\u00EAs (PT)';
                break;

            default:
                this.languageDescription = 'English';
                break;
        }
    }

    getLanguageFlag(country: string): string {
        if (country === undefined || country === null || country === '') {
            country = this.languageService.getLanguage() ?? 'en';
        }

        switch (country) {
            case 'en':
                return 'fi fi-us';
            case 'pt-BR':
                return 'fi fi-br';
            case 'pt':
            case 'pt-PT':
                return 'fi fi-pt';
            default:
                return 'fi fi-us';
        }
    }

    reloadPage(): void {
        window.location.reload();
    }

    goToStudio() {
        this.sso.getSsoLink().subscribe(res => {
            window.location.href = res.url;
        });
    }
}
